import { getBorder } from "@client/lib/border";
import { registerGoogleFont } from "@client/lib/fonts";
import { EMPTY_OBJECT } from "@client/lib/misc";
import {
  getInherittedSettingValue,
  getScreenSettings,
  mergeCompositeSetting,
  mergeInheritedValues
} from "@client/lib/settings";
import { getBackground, getBoxShadow, getMargin, getPadding, getTextStyle } from "@client/lib/style";
import { STYLE_SHEET_IDS } from "@client/lib/styleSheet";
import {
  type CompositeSettings,
  type GenericMap,
  type WithId,
  type WithSettings,
  type WithStyleSheet
} from "@client/types";
import { joinDefined } from "@common/lib/misc";
import { createSelector } from "reselect";
import {
  combinedSettingsSelector,
  getBackgroundSettingSelector,
  getBorderSettingSelector,
  getColorSelector,
  getSettingValueSelector,
  getShadowSettingSelector
} from ".";
import { createShallowEqualSelector } from "../common";
import { entitiesSelector } from "../entities";
import { idPropsSelector } from "../props";
import { screenSelector, styleSheetDataLocalSelector, styleSheetDataSettingsSelector } from "../state";
import { type OutputParametricSelector } from "../types";
import { getTextSettingSelector } from "./text";

export const makeTextStyleSheetColorSelector = (target: string, prefix?: string) =>
  getColorSelector(target, "color", joinDefined(".", prefix, "text"), "text");

export const makeTextStyleSheetSettingSelector = (target: string, prefix?: string) => {
  const valueSelector = getSettingValueSelector(target, "s_text", joinDefined(".", prefix, "text"));

  return createSelector(
    [styleSheetDataSettingsSelector, styleSheetDataLocalSelector, valueSelector, screenSelector],
    (styleSheetSettings, localStyleSheetSettings, value, screen) => {
      if (value === STYLE_SHEET_IDS.custom) return;

      const finalValue = value && value in styleSheetSettings ? value : STYLE_SHEET_IDS.defaults.text;
      return mergeCompositeSetting<CompositeSettings.Text>(
        (screen) => getScreenSettings(styleSheetSettings[finalValue], screen),
        localStyleSheetSettings && localStyleSheetSettings[finalValue]
          ? (screen) => getScreenSettings(localStyleSheetSettings[finalValue], screen)
          : null,
        screen
      );
    }
  );
};

export const makeTextStyleSelector = (target: string, prefix?: string) => {
  const colorSelector = makeTextStyleSheetColorSelector(target, prefix);
  const styleSheetSettingSelector = makeTextStyleSheetSettingSelector(target, prefix);
  const settingSelector = getTextSettingSelector(target, prefix);

  return createSelector(
    [colorSelector, styleSheetSettingSelector, settingSelector],
    (color, styleSheetSetting, setting) => {
      const value = {
        ...mergeInheritedValues<CompositeSettings.Text>(setting, styleSheetSetting ?? {}),
        s_text: STYLE_SHEET_IDS.custom
      };

      registerGoogleFont(value.font);

      return {
        ...getTextStyle(value),
        color
      };
    }
  );
};

export const makeBackgroundStyleSelector = (target: string, prefix?: string) => {
  const compositeSelector = getBackgroundSettingSelector(target, prefix);
  return createSelector([compositeSelector], getBackground);
};

export const makeMarginStyleSelector = (target: string, prefix?: string) => {
  const settingSelector = getSettingValueSelector(target, "margin", prefix);
  return createShallowEqualSelector([settingSelector], getMargin);
};

export const makeBoxShadowStyleSelector = (target: string, prefix?: string) => {
  const compositeSelector = getShadowSettingSelector(target, prefix);
  return createSelector([compositeSelector], (x) => getBoxShadow(x as any));
};

export const makeBorderStyleSelector = (target: string, prefix?: string) => {
  const compositeSelector = getBorderSettingSelector(target, prefix);
  return createSelector([compositeSelector], getBorder);
};

// TODO: style isn't needed. Can use the shorthand
export const makePaddingStyleSelector = (target: string, prefix?: string) => {
  const settingSelector = getSettingValueSelector(target, "padding", prefix);
  return createShallowEqualSelector([settingSelector], (x) => getPadding(x as any));
};

export type CombinedStyleSelectorName = "background" | "border" | "boxShadow" | "margin" | "padding" | "text";
type MakeSelector = (target: string, prefix?: string) => any;
const SELECTOR_NAME_TO_MAKE_SELECTOR: GenericMap<MakeSelector> = {
  background: makeBackgroundStyleSelector,
  border: makeBorderStyleSelector,
  boxShadow: makeBoxShadowStyleSelector,
  margin: makeMarginStyleSelector,
  padding: makePaddingStyleSelector,
  text: makeTextStyleSelector
};
export const combinedStyleSelector = (
  target: string,
  selectorNames: CombinedStyleSelectorName[],
  prefix?: string
): OutputParametricSelector<WithSettings | WithStyleSheet, void | WithId, any, any> =>
  createSelector(
    selectorNames.map((name) => SELECTOR_NAME_TO_MAKE_SELECTOR[name]!(target, prefix)),
    (...values): React.CSSProperties => {
      const v = values.filter((x) => !!x);
      return v.length
        ? (v.reduce((res: object, s: object) => ({ ...res, ...(s || {}) }), {}) as React.CSSProperties)
        : EMPTY_OBJECT;
    }
  );

export const makeSizeStyleSelector = (target: string) => {
  const settingsSelector = combinedSettingsSelector(target, [
    "height",
    "width",
    "minHeight",
    "minWidth",
    "maxHeight",
    "maxWidth"
  ]);
  const parentSettingsSelector = createSelector([entitiesSelector, idPropsSelector], (entities, id) =>
    entities[id].parentId ? entities[entities[id].parentId as any].settings : undefined
  );
  const parentDirectionSelector = createSelector([parentSettingsSelector, screenSelector], (parentSettings, screen) =>
    parentSettings ? getInherittedSettingValue(parentSettings, "", "direction", screen) : undefined
  );

  return createSelector(
    [settingsSelector, parentDirectionSelector],
    (settings, parentDirection): React.CSSProperties => {
      const { width, height, ...styleSettings } = settings;
      const style: React.CSSProperties = styleSettings;

      switch (width) {
        case "fill": {
          if (parentDirection === "column") {
            style.alignSelf = "stretch";
          } else {
            style.flex = 1;
          }
          break;
        }
        case "hug": {
          style.width = "fit-content";
          break;
        }
        default: {
          style.width = width;
          break;
        }
      }
      switch (height) {
        case "fill": {
          if (parentDirection === "column") {
            style.height = "1px";
            style.flex = "1 0 0px";
          } else {
            style.alignSelf = "stretch";
          }
          break;
        }
        case "hug": {
          style.height = "fit-content";
          break;
        }
        default: {
          style.height = height;
          break;
        }
      }

      return style;
    }
  );
};
